import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Store,
  Gift,
  BarChart3,
  Monitor,
  ListChecks,
  Megaphone,
  CreditCard,
  TrendingUp,
  Settings,
  LogOut,
  Sparkles,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  Loader2,
  ArrowDownLeft,
  ArrowUpRight,
  Eye,
  Receipt,
  Radio,
  Copy,
  ShieldCheck,
  ShieldX,
  Clock,
  AlertOctagon,
  Zap
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
  { icon: FileText, label: "Registrations", path: "/admin/registrations" },
  { icon: AlertTriangle, label: "OCR Exceptions", path: "/admin/ocr-exceptions" },
  { icon: Store, label: "Retailers", path: "/admin/retailers" },
  { icon: Gift, label: "Rewards", path: "/admin/rewards" },
  { icon: Sparkles, label: "Entitlements", path: "/admin/entitlements-v2" },
  { icon: Receipt, label: "Receipts", path: "/admin/receipts" },
  { icon: Car, label: "ANPR Events", path: "/admin/anpr" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Monitor, label: "Screens", path: "/admin/screens" },
  { icon: ListChecks, label: "Playlists", path: "/admin/playlists" },
  { icon: Megaphone, label: "Advertisers", path: "/admin/advertisers" },
  { icon: TrendingUp, label: "Campaigns", path: "/admin/campaigns" },
  { icon: CreditCard, label: "Billing", path: "/admin/billing" },
  { icon: TrendingUp, label: "Revenue", path: "/admin/revenue" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
];

export default function AdminANPR() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailDialog, setDetailDialog] = useState({ open: false, event: null, decision: null, providerLog: null });
  const [activeTab, setActiveTab] = useState("events");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const eventParams = new URLSearchParams();
      if (eventTypeFilter !== "all") eventParams.append("event_type", eventTypeFilter);
      if (statusFilter !== "all") eventParams.append("status", statusFilter);
      eventParams.append("limit", "100");
      
      const [eventsRes, decisionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/anpr/events?${eventParams.toString()}`, { withCredentials: true }),
        axios.get(`${API}/anpr/decisions?limit=50`, { withCredentials: true }),
        axios.get(`${API}/anpr/stats`, { withCredentials: true })
      ]);
      
      setEvents(eventsRes.data?.events || []);
      setDecisions(decisionsRes.data?.decisions || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired, please login again");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [eventTypeFilter, statusFilter, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    document.cookie = "admin_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/admin");
  };

  const fetchEventDetails = async (eventId) => {
    try {
      const res = await axios.get(`${API}/anpr/events/${eventId}`, { withCredentials: true });
      setDetailDialog({
        open: true,
        event: res.data.event,
        decision: res.data.decision,
        providerLog: res.data.provider_log
      });
    } catch (error) {
      toast.error("Failed to fetch event details");
    }
  };

  const getEventTypeBadge = (eventType) => {
    if (eventType === "entry") {
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <ArrowDownLeft className="w-3 h-3" />
          Entry
        </span>
      );
    }
    if (eventType === "exit") {
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
          <ArrowUpRight className="w-3 h-3" />
          Exit
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        {eventType}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      processed: { bg: "bg-blue-100", text: "text-blue-700", icon: CheckCircle, label: "Processed" },
      approved: { bg: "bg-green-100", text: "text-green-700", icon: ShieldCheck, label: "Approved" },
      denied: { bg: "bg-red-100", text: "text-red-700", icon: ShieldX, label: "Denied" },
      duplicate: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Copy, label: "Duplicate" },
      ignored: { bg: "bg-slate-100", text: "text-slate-600", icon: AlertOctagon, label: "Ignored" },
      failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle, label: "Failed" },
      received: { bg: "bg-purple-100", text: "text-purple-700", icon: Radio, label: "Received" }
    };
    
    const badge = badges[status] || badges.received;
    const Icon = badge.icon;
    
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getDecisionBadge = (decision) => {
    const badges = {
      allow: { bg: "bg-green-500/20", text: "text-green-400", icon: ShieldCheck, label: "Allow" },
      deny: { bg: "bg-red-500/20", text: "text-red-400", icon: ShieldX, label: "Deny" },
      require_payment: { bg: "bg-yellow-500/20", text: "text-yellow-400", icon: CreditCard, label: "Require Payment" },
      manual_review: { bg: "bg-orange-500/20", text: "text-orange-400", icon: AlertTriangle, label: "Manual Review" }
    };
    
    const badge = badges[decision] || badges.deny;
    const Icon = badge.icon;
    
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = !searchTerm || 
      event.vehicle_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.event_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const filteredDecisions = decisions.filter(decision => {
    const matchesSearch = !searchTerm || 
      decision.vehicle_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      decision.decision_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-anpr">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #10B981 100%)' }}
            >
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">ParkWise</h1>
              <p className="text-xs text-slate-500">Admin Console</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === "/admin/anpr";
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-500/20 text-blue-300"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Radio className="w-8 h-8 text-blue-400" />
                ANPR Orchestration
              </h1>
              <p className="text-slate-400 mt-1">
                Live camera events, decisions, and barrier control
              </p>
            </div>
            <Button 
              onClick={fetchData} 
              variant="outline" 
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <ArrowDownLeft className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.entry_events || 0}</p>
                      <p className="text-xs text-slate-400">Entries Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.exit_events || 0}</p>
                      <p className="text-xs text-slate-400">Exits Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.approved || 0}</p>
                      <p className="text-xs text-slate-400">Approved</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <ShieldX className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.denied || 0}</p>
                      <p className="text-xs text-slate-400">Denied</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <Copy className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.duplicates || 0}</p>
                      <p className="text-xs text-slate-400">Duplicates</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* MOCKED indicator */}
          {stats?.is_mocked && (
            <div className="mb-6 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-400" />
              <span className="text-orange-400 text-sm font-medium">
                ANPR System is in MOCKED mode - simulating camera events
              </span>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-slate-900 border border-slate-800">
              <TabsTrigger 
                value="events" 
                className="data-[state=active]:bg-slate-800 data-[state=active]:text-white"
                data-testid="events-tab"
              >
                Events ({filteredEvents.length})
              </TabsTrigger>
              <TabsTrigger 
                value="decisions" 
                className="data-[state=active]:bg-slate-800 data-[state=active]:text-white"
                data-testid="decisions-tab"
              >
                Decisions ({filteredDecisions.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Filters */}
          <Card className="bg-slate-900 border-slate-800 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Search by vehicle or event ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="search-input"
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
                
                {activeTab === "events" && (
                  <>
                    <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                      <SelectTrigger className="w-[130px] bg-slate-800 border-slate-700 text-white" data-testid="event-type-filter">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="all" className="text-white">All Types</SelectItem>
                        <SelectItem value="entry" className="text-white">Entry</SelectItem>
                        <SelectItem value="exit" className="text-white">Exit</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[140px] bg-slate-800 border-slate-700 text-white" data-testid="status-filter">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="all" className="text-white">All Status</SelectItem>
                        <SelectItem value="processed" className="text-white">Processed</SelectItem>
                        <SelectItem value="approved" className="text-white">Approved</SelectItem>
                        <SelectItem value="denied" className="text-white">Denied</SelectItem>
                        <SelectItem value="duplicate" className="text-white">Duplicate</SelectItem>
                        <SelectItem value="ignored" className="text-white">Ignored</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Events Table */}
          {activeTab === "events" && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="border-b border-slate-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <Radio className="w-5 h-5" />
                  ANPR Events
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <div className="text-center py-12">
                    <Radio className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No ANPR events found</p>
                    <p className="text-slate-500 text-sm mt-1">
                      Events are logged when cameras detect vehicles
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800/50">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Time
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Vehicle
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Lane
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Provider
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Barrier
                          </th>
                          <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {filteredEvents.map((event) => (
                          <tr 
                            key={event.event_id} 
                            className="hover:bg-slate-800/30 transition-colors"
                            data-testid={`event-row-${event.event_id}`}
                          >
                            <td className="px-4 py-3 text-slate-300 text-sm">
                              {formatDateTime(event.created_at)}
                            </td>
                            <td className="px-4 py-3">
                              {getEventTypeBadge(event.event_type)}
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-mono font-semibold text-white">
                                {event.vehicle_registration || "—"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-300">
                              {event.lane_id}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-slate-400 uppercase">
                                {event.provider}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {getStatusBadge(event.status)}
                            </td>
                            <td className="px-4 py-3">
                              {event.barrier_action ? (
                                <span className={`text-xs font-medium ${
                                  event.barrier_action === "open" ? "text-green-400" : "text-slate-400"
                                }`}>
                                  {event.barrier_action.toUpperCase()}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-500">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-white"
                                onClick={() => fetchEventDetails(event.event_id)}
                                data-testid={`view-btn-${event.event_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Decisions Table */}
          {activeTab === "decisions" && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="border-b border-slate-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5" />
                  Exit Decisions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  </div>
                ) : filteredDecisions.length === 0 ? (
                  <div className="text-center py-12">
                    <ShieldCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No decisions found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800/50">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Time
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Vehicle
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Lane
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Decision
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Reason
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                            Barrier
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {filteredDecisions.map((decision) => (
                          <tr 
                            key={decision.decision_id} 
                            className="hover:bg-slate-800/30 transition-colors"
                            data-testid={`decision-row-${decision.decision_id}`}
                          >
                            <td className="px-4 py-3 text-slate-300 text-sm">
                              {formatDateTime(decision.created_at)}
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-mono font-semibold text-white">
                                {decision.vehicle_registration}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-300">
                              {decision.lane_id}
                            </td>
                            <td className="px-4 py-3">
                              {getDecisionBadge(decision.decision)}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-400">
                                {decision.reason?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {decision.barrier_action ? (
                                <span className={`text-xs font-medium ${
                                  decision.barrier_action === "open" ? "text-green-400" : "text-slate-400"
                                }`}>
                                  {decision.barrier_action.toUpperCase()}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-500">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => !open && setDetailDialog({ open: false, event: null, decision: null, providerLog: null })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5" />
              ANPR Event Details
            </DialogTitle>
          </DialogHeader>
          {detailDialog.event && (
            <div className="py-4 space-y-6">
              {/* Event Info */}
              <div>
                <p className="text-xs text-slate-500 uppercase mb-3 font-medium">Event Information</p>
                <div className="grid grid-cols-2 gap-4 bg-slate-800/50 rounded-lg p-4">
                  <div>
                    <p className="text-xs text-slate-500">Event ID</p>
                    <p className="text-white font-mono text-sm">{detailDialog.event.event_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Type</p>
                    {getEventTypeBadge(detailDialog.event.event_type)}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Vehicle</p>
                    <p className="text-white font-mono font-bold">{detailDialog.event.vehicle_registration || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    {getStatusBadge(detailDialog.event.status)}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Lane</p>
                    <p className="text-white">{detailDialog.event.lane_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Provider</p>
                    <p className="text-white uppercase">{detailDialog.event.provider}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Timestamp</p>
                    <p className="text-white">{formatDateTime(detailDialog.event.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Barrier Action</p>
                    <p className={detailDialog.event.barrier_action === "open" ? "text-green-400 font-bold" : "text-slate-400"}>
                      {detailDialog.event.barrier_action?.toUpperCase() || "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Decision Info */}
              {detailDialog.decision && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-3 font-medium">Exit Decision</p>
                  <div className="grid grid-cols-2 gap-4 bg-slate-800/50 rounded-lg p-4">
                    <div>
                      <p className="text-xs text-slate-500">Decision</p>
                      {getDecisionBadge(detailDialog.decision.decision)}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Reason</p>
                      <p className="text-white">{detailDialog.decision.reason?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  
                  {/* Checks Summary */}
                  {detailDialog.decision.checks && (
                    <div className="mt-4">
                      <p className="text-xs text-slate-500 mb-2">Authorization Checks</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(detailDialog.decision.checks).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-2 text-sm">
                            {value?.authorized || value?.has_entitlement ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <XCircle className="w-4 h-4 text-slate-500" />
                            )}
                            <span className="text-slate-300 capitalize">{key.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Raw Payload */}
              {detailDialog.event.raw_payload && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-3 font-medium">Raw Payload</p>
                  <pre className="bg-slate-800 rounded-lg p-4 overflow-x-auto text-xs text-slate-300">
                    {JSON.stringify(detailDialog.event.raw_payload, null, 2)}
                  </pre>
                </div>
              )}

              {/* Parsed Payload */}
              {detailDialog.event.parsed_payload && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-3 font-medium">Parsed Fields</p>
                  <pre className="bg-slate-800 rounded-lg p-4 overflow-x-auto text-xs text-slate-300">
                    {JSON.stringify(detailDialog.event.parsed_payload, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
